import { ProductHandle } from "@/types/graphql";

function objectToParams<T>(obj: T) {
  const params = new URLSearchParams();
  for (const [key, value] of Object.entries(obj)) {
    params.set(key, value);
  }
  return params;
}

type CheckoutSuccessPayload = {
  subscription_id: string | number;
  total?: string; // 100.00
  total_in_cents: number; // 100_00
  discount?: string; // 100.00
  discount_in_cents?: number;
  tax?: string;
  tax_in_cents?: number;
  coupon_code?: string;
  product_handle: ProductHandle;
};

export const routes = {
  CHECKOUT: {
    SUCCESS: (payload: CheckoutSuccessPayload) =>
      `/checkout/success?${objectToParams(payload).toString()}`,
    CHECKOUT: "/checkout",
    CHECKOUT_WITH_OFFER: (params: URLSearchParams) =>
      `/checkout?${params.toString()}`,
  },
  NUCALM: "https://nucalm.com/",
  NUCALM_JOIN: "https://nucalm.com/#join",
  LOGIN: "/login",
  SIGNUP: "/signup",
  "404": "/404",
  "500": "/500",
  FORGOT_PASSWORD: "/forgot-password",
  STORE: "https://store.nucalm.com/",
  HOME: "/",
  ACCOUNT: {
    ACCOUNT: "/account",
    AVAILABLE_PLANS: "/account/plans",
    PAYMENT_PROFILES: "/account/payment-profiles",
    CREATE_PAYMENT_PROFILE: "/account/payment-profiles/create",
    CHARGIFY_SUBSCRIPTION_PAYMENT: (id: number, token: string) =>
      process.env.NEXT_PUBLIC_VERCEL_ENV === "production"
        ? `https://solace-lifesciences-inc.chargifypay.com/update_payment/${id}/${token}`
        : `https://staging-solace-lifesciences-inc.chargifypay.com/update_payment/${id}/${token}`,
    SHIPPING_ADDRESS: "/account/shipping-address",
    ACCOUNT_DETAILS: "/account/account-details",
    INVOICES: "/account/invoices",
    INVOICE: (id: string) => `${routes.ACCOUNT.INVOICES}/${id}`,
    REFER: `/account/refer`,
    PURCHASE_DISCS: `/account/purchase-discs`,
    MANAGE_PLAN: (subscriptionId: number) =>
      `/account/plans/${subscriptionId}/manage`,
    MANAGE_PLAN_PAYMENT: (subscriptionId: number) =>
      `/account/plans/${subscriptionId}/payment`,
    CHANGE_PLAN: (subscriptionId: string, handle: string) =>
      `/account/plans/${subscriptionId}/change/${handle}`,
    CANCEL_PLAN: (subscriptionId: string) =>
      `/account/plans/${subscriptionId}/cancel`,
    REACTIVATE_PLAN: (subscriptionId: string) =>
      `/account/plans/${subscriptionId}/reactivate`,
    PROMOTIONS: "/account/promotions",
    FAMILY_PLAN: "/account/family-plan",
    PAUSE_DISCS: (id: string) => `/account/plans/${id}/pause-discs`,
    RETRY: (id: string) => `/account/plans/${id}/retry`,
    DELETE: "/account/delete",
  },
  PRODUCTS: {
    GIFT_CARDS: "https://nucalm.com/products/gift-cards",
  },
  PRIVACY_POLICY: "https://nucalm.com/privacy-policy",
  PROGRAM_CREDITS: "https://nucalm.com/program-credits",
  TERMS_OF_SERVICE: "https://nucalm.com/terms-of-service",
  SUPPORT: "https://support.nucalm.com/",
  PHONE_NUMBER: "tel:+18008101701",
  SUPPORT_EMAIL: "mailto:support@nucalm.com",
  WEBSITE: {
    PRODUCTS_PRICING: "https://nucalm.com/products-pricing#subscriptionPlan",
  },
  MEETING_LINKS: {
    BETHANY: "https://meetings.hubspot.com/bethany-piziks",
  },
};

const objectToString: any = (obj: any) => {
  return Object.keys(obj)
    .map((key) => {
      if (typeof obj[key] === "object") {
        const value = obj[key];
        return objectToString(value);
      }
      return `${key}=${obj[key]}`;
    })
    .join("&");
};

export const staticRoutes = () => {
  const invalidRoutes = [
    "BLOG",
    "RELEASES",
    "ACCOUNT",
    "PHONE_NUMBER",
    "SUPPORT_EMAIL",
    "SUPPORT",
    "PODCASTS",
  ];

  let obj: any = {};
  Object.keys(routes).map((k) => {
    if (!invalidRoutes.includes(k)) {
      // @ts-ignore
      obj[k] = routes[k];
    }
  });

  const staticPagesStr: string = objectToString(obj);

  const ret: string[] = staticPagesStr.split("&").map((str: string) => {
    const [_key, value] = str.split("=");
    return value;
  });

  return ret;
};

export const contactInformation = {
  PHONE_NUMBER: "1.800.810.1701",
  EMAIL: "support@nucalm.com",
};

enum ACCOUNT_PAGES {
  ACCOUNT = "Account Overview",
  AVAILABLE_PLANS = "Available Plans",
  PURCHASE_DISCS = "Purchase Discs",
  PAYMENT_PROFILES = "Billing Details",
  ACCOUNT_DETAILS = "Account Details",
  INVOICES = "Invoices",
  // SUBSCRIPTIONS = "Subscriptions",
  SHIPPING_ADDRESSES = "Shipping Address",
  PROMOTIONS = "Promotions",
  FAMILY_PLAN = "Family Plan",
  PAUSE_DISCS = "Pause Discs",
  STORE = "Store",
}

export const accountPages = new Map([
  [ACCOUNT_PAGES.ACCOUNT, routes.ACCOUNT.ACCOUNT],
  [ACCOUNT_PAGES.AVAILABLE_PLANS, routes.ACCOUNT.AVAILABLE_PLANS],
  [ACCOUNT_PAGES.ACCOUNT_DETAILS, routes.ACCOUNT.ACCOUNT_DETAILS],
  [ACCOUNT_PAGES.INVOICES, routes.ACCOUNT.INVOICES],
  [ACCOUNT_PAGES.STORE, routes.STORE],
  [ACCOUNT_PAGES.PURCHASE_DISCS, routes.ACCOUNT.PURCHASE_DISCS],
  // [ACCOUNT_PAGES.SUBSCRIPTIONS, routes.ACCOUNT.SUBSCRIPTIONS],
  [ACCOUNT_PAGES.SHIPPING_ADDRESSES, routes.ACCOUNT.SHIPPING_ADDRESS],
  [ACCOUNT_PAGES.PROMOTIONS, routes.ACCOUNT.PROMOTIONS],
  [ACCOUNT_PAGES.FAMILY_PLAN, routes.ACCOUNT.FAMILY_PLAN],
  // [ACCOUNT_PAGES.PAUSE_DISCS, routes.ACCOUNT.PAUSE_DISCS],
]);

export const cookies = {
  COOKIE_CONSENT: "cookie-consent",
  COOKIE_USER: "cookie-user",
  SESSION: "session",
};

export const analytics = {
  GOOGLE_TAG_MANAGER_ID: "GTM-PMX7PVG",
  UA_ID: "UA-15199707-2",
  GA4_ID: "G-RJXWH5SK70",
};

export const affiliates = {
  SHARE_A_SALE: {
    MERCHANT_ID: "100346",
    URL: "https://shareasale.com/sale.cfm",
  },
};

export const kwikStatePlansToHide = ["kwikstate_annual"];
